/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import {
  createDurationFormatter,
  getNumberFormatter,
  getNumberFormatterRegistry,
  NumberFormats,
  NumberFormatter,
  getTimeFormatterRegistry,
  smartDateFormatter,
  smartDateVerboseFormatter,
} from '@superset-ui/core';
import { FormatLocaleDefinition } from 'd3-format';

export function formatDuration(
  days: number,
  hours: number,
  minutes: number,
  seconds: number,
): string {
  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}天`);
  }
  if (hours > 0 || parts.length > 0) {
    parts.push(`${hours}小时`);
  }
  if (minutes > 0 || parts.length > 0) {
    parts.push(`${minutes}分`);
  }
  parts.push(`${seconds}秒`);

  return parts.join('');
}

function createWanyuanFormatter(formatString: string): NumberFormatter {
  const numberFormatter = getNumberFormatter(formatString);
  const formatter: NumberFormatter = ((value: number) =>
    `${numberFormatter(value / 10000)}万`) as unknown as NumberFormatter;
  Object.assign(formatter, {
    id: `wanyuan_${formatString}`,
    label: `万元格式化 (${formatString})`,
    description: `将数值转换为万元并使用 ${formatString} 格式化`,
    isInvalid: false,
    format: numberFormatter.format,
    preview: (value?: number) =>
      `${numberFormatter((value ?? 10000) / 10000)}万`,
    apply: (value: number) => `${numberFormatter(value / 10000)}万`,
  });

  return formatter;
}

function createYuanFormatter(formatString: string): NumberFormatter {
  const numberFormatter = getNumberFormatter(formatString);
  const formatter: NumberFormatter = ((value: number) =>
    `¥${numberFormatter(value)}`) as unknown as NumberFormatter;
  Object.assign(formatter, {
    id: `yuan_${formatString}`,
    label: `人民币格式化 (${formatString})`,
    description: `将数值格式化为人民币`,
    isInvalid: false,
    format: numberFormatter.format,
    preview: (value?: number) => `¥${numberFormatter(value ?? 12345.432)}`,
    apply: (value: number) => `¥${numberFormatter(value)}`,
  });

  return formatter;
}

function createCustomDurationFormatter(): NumberFormatter {
  const formatter: NumberFormatter = ((value: number) => {
    let remainingValue = value;
    const days = Math.floor(remainingValue / (3600 * 24));
    remainingValue %= 3600 * 24;
    const hours = Math.floor(remainingValue / 3600);
    remainingValue %= 3600;
    const minutes = Math.floor(remainingValue / 60);
    const seconds = Math.floor(remainingValue % 60);

    return formatDuration(days, hours, minutes, seconds);
  }) as unknown as NumberFormatter;

  Object.assign(formatter, {
    id: 'custom_duration',
    label: '自定义时长格式化',
    description: '将秒数格式化为xx天xx小时xx分xx秒',
    isInvalid: false,
    format: (value: number) => {
      let remainingValue = value;
      const days = Math.floor(remainingValue / (3600 * 24));
      remainingValue %= 3600 * 24;
      const hours = Math.floor(remainingValue / 3600);
      remainingValue %= 3600;
      const minutes = Math.floor(remainingValue / 60);
      const seconds = Math.floor(remainingValue % 60);

      return formatDuration(days, hours, minutes, seconds);
    },
    preview: (value?: number) => {
      const val = value ?? 1235532324;
      let remainingValue = val;
      const days = Math.floor(remainingValue / (3600 * 24));
      remainingValue %= 3600 * 24;
      const hours = Math.floor(remainingValue / 3600);
      remainingValue %= 3600;
      const minutes = Math.floor(remainingValue / 60);
      const seconds = Math.floor(remainingValue % 60);

      return formatDuration(days, hours, minutes, seconds);
    },
    apply: (value: number) => {
      let remainingValue = value;
      const days = Math.floor(remainingValue / (3600 * 24));
      remainingValue %= 3600 * 24;
      const hours = Math.floor(remainingValue / 3600);
      remainingValue %= 3600;
      const minutes = Math.floor(remainingValue / 60);
      const seconds = Math.floor(remainingValue % 60);

      return formatDuration(days, hours, minutes, seconds);
    },
  });

  return formatter;
}

function createDurationHmsFormatter(): NumberFormatter {
  const formatHms = (value: number): string => {
    const hours = Math.floor(value / 3600);
    const remainingAfterHours = value % 3600;
    const minutes = Math.floor(remainingAfterHours / 60);
    const seconds = Math.floor(remainingAfterHours % 60);
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(
      seconds,
    ).padStart(2, '0')}`;
  };

  const formatter: NumberFormatter = ((value: number) =>
    formatHms(value)) as unknown as NumberFormatter;

  Object.assign(formatter, {
    id: 'duration_hms',
    label: '时长格式化 (HH:MM:SS)',
    description: '将秒数格式化为小时:分钟:秒',
    isInvalid: false,
    format: formatHms,
    preview: (value?: number) => formatHms(value ?? 54694),
    apply: formatHms,
  });

  return formatter;
}

function createDurationMsFormatter(): NumberFormatter {
  const formatDurationMs = (value: number): string => {
    const ms = Math.floor(value % 1000);
    const totalSeconds = Math.floor(value / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}天`);
    if (hours > 0 || parts.length > 0) parts.push(`${hours}小时`);
    if (minutes > 0 || parts.length > 0) parts.push(`${minutes}分`);
    parts.push(`${seconds}秒`);
    if (ms > 0) parts.push(`${ms}毫秒`);

    return parts.join('');
  };

  const formatter: NumberFormatter = ((value: number) =>
    formatDurationMs(value)) as unknown as NumberFormatter;

  Object.assign(formatter, {
    id: 'duration_ms',
    label: '时长格式化（带毫秒）',
    description: '将毫秒数格式化为xx天xx小时xx分xx秒xx毫秒',
    isInvalid: false,
    format: formatDurationMs,
    preview: (value?: number) => formatDurationMs(value ?? 123456),
    apply: formatDurationMs,
  });

  return formatter;
}

function createDurationHmsMsFormatter(): NumberFormatter {
  const formatHmsMs = (value: number): string => {
    const ms = Math.floor(value % 1000);
    const totalSeconds = Math.floor(value / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const h = String(hours).padStart(2, '0');
    const m = String(minutes).padStart(2, '0');
    const s = String(seconds).padStart(2, '0');
    const msStr = String(ms).padStart(3, '0');

    return `${h}:${m}:${s}.${msStr}`;
  };

  const formatter: NumberFormatter = ((value: number) =>
    formatHmsMs(value)) as unknown as NumberFormatter;

  Object.assign(formatter, {
    id: 'duration_hms_ms',
    label: '时长格式化 (HH:MM:SS.mmm)',
    description: '将毫秒数格式化为小时:分钟:秒.毫秒',
    isInvalid: false,
    format: formatHmsMs,
    preview: (value?: number) => formatHmsMs(value ?? 123456.789),
    apply: formatHmsMs,
  });

  return formatter;
}

function createScientificFormatter(precision: number): NumberFormatter {
  const formatScientific = (value: number): string =>
    value.toExponential(precision);

  const formatter: NumberFormatter = ((value: number) =>
    formatScientific(value)) as unknown as NumberFormatter;

  Object.assign(formatter, {
    id: `scientific_${precision}`,
    label: `科学计数法 (${precision}位小数)`,
    description: '将数值格式化为科学计数法',
    isInvalid: false,
    format: formatScientific,
    preview: (value?: number) => formatScientific(value ?? 12345),
    apply: formatScientific,
  });

  return formatter;
}

function createChineseNumberFormatter(): NumberFormatter {
  const digits = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
  const units = ['', '十', '百', '千'];
  const largeUnits = ['', '万', '亿', '兆'];

  const formatChineseNumber = (value: number): string => {
    if (value === 0) return '零';
    if (value < 0) return `负${formatChineseNumber(-value)}`;

    const intPart = Math.floor(value);
    const decPart = value - intPart;

    const formatInt = (numParam: number): string => {
      if (numParam === 0) return '';
      let result = '';
      let unitIndex = 0;
      let num = numParam;

      while (num > 0) {
        const section = num % 10000;
        let sectionStr = '';
        let tempSection = section;

        for (let i = 0; i < 4 && tempSection > 0; i += 1) {
          const digit = tempSection % 10;
          if (digit !== 0) {
            sectionStr = `${digits[digit]}${units[i]}${sectionStr}`;
          } else if (sectionStr.length > 0 && !sectionStr.startsWith('零')) {
            sectionStr = `零${sectionStr}`;
          }
          tempSection = Math.floor(tempSection / 10);
        }

        if (section > 0) {
          result = `${sectionStr}${largeUnits[unitIndex]}${result}`;
        }
        num = Math.floor(num / 10000);
        unitIndex += 1;
      }

      return result.replace(/零+/g, '零').replace(/零$/, '');
    };

    let result = formatInt(intPart);

    if (decPart > 0) {
      result += '点';
      const decStr = String(decPart).slice(2);
      result += Array.from(decStr)
        .map(char => digits[parseInt(char, 10)])
        .join('');
    }

    return result;
  };

  const formatter: NumberFormatter = ((value: number) =>
    formatChineseNumber(value)) as unknown as NumberFormatter;

  Object.assign(formatter, {
    id: 'chinese_number',
    label: '中文数字格式化',
    description: '将数值转换为中文数字',
    isInvalid: false,
    format: formatChineseNumber,
    preview: (value?: number) => formatChineseNumber(value ?? 12345),
    apply: formatChineseNumber,
  });

  return formatter;
}

function createFileSizeFormatter(): NumberFormatter {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = bytes / Math.pow(k, i);
    return `${size.toFixed(2)} ${units[i]}`;
  };

  const formatter: NumberFormatter = ((value: number) =>
    formatFileSize(value)) as unknown as NumberFormatter;

  Object.assign(formatter, {
    id: 'file_size',
    label: '文件大小格式化',
    description: '将字节数格式化为 B/KB/MB/GB/TB',
    isInvalid: false,
    format: formatFileSize,
    preview: (value?: number) => formatFileSize(value ?? 1234567890),
    apply: formatFileSize,
  });

  return formatter;
}

function createRmbUpperFormatter(): NumberFormatter {
  const digits = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
  const units = ['', '拾', '佰', '仟'];
  const largeUnits = ['', '万', '亿', '兆'];

  const formatRmbUpper = (value: number): string => {
    if (value === 0) return '零元整';
    if (value < 0) return `负${formatRmbUpper(-value)}`;

    const intPart = Math.floor(value);
    const decPart = Math.round((value - intPart) * 100);

    const formatInt = (numParam: number): string => {
      if (numParam === 0) return '';
      let result = '';
      let unitIndex = 0;
      let num = numParam;

      while (num > 0) {
        const section = num % 10000;
        let sectionStr = '';
        const sectionNum = section;
        let tempSection = section;

        for (let i = 0; i < 4 && tempSection > 0; i += 1) {
          const digit = tempSection % 10;
          if (digit !== 0) {
            sectionStr = `${digits[digit]}${units[i]}${sectionStr}`;
          } else if (sectionStr.length > 0 && !sectionStr.startsWith('零')) {
            sectionStr = `零${sectionStr}`;
          }
          tempSection = Math.floor(tempSection / 10);
        }

        if (sectionNum > 0) {
          result = `${sectionStr}${largeUnits[unitIndex]}${result}`;
        }
        num = Math.floor(num / 10000);
        unitIndex += 1;
      }

      return result.replace(/零+/g, '零').replace(/零$/, '');
    };

    let result = `${formatInt(intPart)}元`;

    if (decPart === 0) {
      result += '整';
    } else {
      const jiao = Math.floor(decPart / 10);
      const fen = decPart % 10;
      if (jiao > 0) result += `${digits[jiao]}角`;
      if (fen > 0) result += `${digits[fen]}分`;
    }

    return result;
  };

  const formatter: NumberFormatter = ((value: number) =>
    formatRmbUpper(value)) as unknown as NumberFormatter;

  Object.assign(formatter, {
    id: 'rmb_upper',
    label: '人民币大写格式化',
    description: '将数值转换为人民币大写',
    isInvalid: false,
    format: formatRmbUpper,
    preview: (value?: number) => formatRmbUpper(value ?? 12345),
    apply: formatRmbUpper,
  });

  return formatter;
}

export default function setupFormatters(
  d3Format: Partial<FormatLocaleDefinition>,
) {
  getNumberFormatterRegistry()
    .setD3Format(d3Format)
    // Add shims for format strings that are deprecated or common typos.
    // Temporary solution until performing a db migration to fix this.
    .registerValue(',0', getNumberFormatter(',.4~f'))
    .registerValue('null', getNumberFormatter(',.4~f'))
    .registerValue('%', getNumberFormatter('.0%'))
    .registerValue('.', getNumberFormatter('.4~f'))
    .registerValue(',f', getNumberFormatter(',d'))
    .registerValue(',r', getNumberFormatter(',.4f'))
    .registerValue('0f', getNumberFormatter(',d'))
    .registerValue(',#', getNumberFormatter(',.4~f'))
    .registerValue('$,f', getNumberFormatter('$,d'))
    .registerValue('0%', getNumberFormatter('.0%'))
    .registerValue('f', getNumberFormatter(',d'))
    .registerValue(',.', getNumberFormatter(',.4~f'))
    .registerValue('.1%f', getNumberFormatter('.1%'))
    .registerValue('1%', getNumberFormatter('.0%'))
    .registerValue('3%', getNumberFormatter('.0%'))
    .registerValue(',%', getNumberFormatter(',.0%'))
    .registerValue('.r', getNumberFormatter('.4~f'))
    .registerValue('$,.0', getNumberFormatter('$,d'))
    .registerValue('$,.1', getNumberFormatter('$,.1~f'))
    .registerValue(',0s', getNumberFormatter(',.4~f'))
    .registerValue('%%%', getNumberFormatter('.0%'))
    .registerValue(',0f', getNumberFormatter(',d'))
    .registerValue('+,%', getNumberFormatter('+,.0%'))
    .registerValue('$f', getNumberFormatter('$,d'))
    .registerValue('+,', getNumberFormatter(NumberFormats.INTEGER_SIGNED))
    .registerValue(',2f', getNumberFormatter(',.4~f'))
    .registerValue(',g', getNumberFormatter(',.4~f'))
    .registerValue('int', getNumberFormatter(NumberFormats.INTEGER))
    .registerValue('.0%f', getNumberFormatter('.1%'))
    .registerValue('$,0', getNumberFormatter('$,.4f'))
    .registerValue('$,0f', getNumberFormatter('$,.4f'))
    .registerValue('$,.f', getNumberFormatter('$,.4f'))
    // 人民币格式化
    .registerValue('yuan_2', createYuanFormatter(',.2f'))
    .registerValue('yuan_0', createYuanFormatter(',.0f'))
    .registerValue('rmb_upper', createRmbUpperFormatter())
    // 文件大小格式化
    .registerValue('file_size', createFileSizeFormatter())
    .registerValue('DURATION', createDurationFormatter())
    // 万元格式化
    .registerValue('wanyuan_0', createWanyuanFormatter(',.0f'))
    .registerValue('wanyuan_1', createWanyuanFormatter(',.1f'))
    .registerValue('wanyuan_2', createWanyuanFormatter(',.2f'))
    .registerValue('wanyuan_3', createWanyuanFormatter(',.3f'))
    .registerValue('wanyuan_4', createWanyuanFormatter(',.4f'))
    // 自定义时长格式化
    .registerValue('custom_duration', createCustomDurationFormatter())
    .registerValue('duration_hms', createDurationHmsFormatter())
    .registerValue('duration_ms', createDurationMsFormatter())
    .registerValue('duration_hms_ms', createDurationHmsMsFormatter())
    .registerValue('scientific_2', createScientificFormatter(2))
    .registerValue('chinese_number', createChineseNumberFormatter())
    .registerValue(
      'DURATION_SUB',
      createDurationFormatter({ formatSubMilliseconds: true }),
    );

  getTimeFormatterRegistry()
    .registerValue('smart_date', smartDateFormatter)
    .registerValue('smart_date_verbose', smartDateVerboseFormatter)
    .setDefaultKey('smart_date');
}
